import { getCurrencySymbol, formatCurrency } from '@angular/common';
import { getISODay } from 'date-fns/getISODay';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { parse } from 'date-fns/parse';
import { toZonedTime } from 'date-fns-tz';
import { format as _format } from 'date-fns/format';

import { Package, Availability, Venue } from '../models/venues.model';

type MaybeNumber = number | string | null | undefined;

type DiscountResult = {
    price: number;
    price_group: number | null;
};

const numberOrDefault = (...values: MaybeNumber[]): number => {
    for (const value of values) {
        if (value === null || value === undefined || value === '') {
            continue;
        }

        const parsed = +value;

        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return 0;
};

const applyDiscount = (
    promo: any,
    price: number,
    price_group?: number | null,
    qty?: number
): DiscountResult => {
    const normalizedPriceGroup = price_group === undefined || price_group === null ? null : +price_group;

    if (qty === 0) {
        return { price, price_group: normalizedPriceGroup };
    }

    if (+promo?.promo_percent) {
        const pct = (100 - +promo.promo_percent) / 100;
        return {
            price: price * pct,
            price_group: normalizedPriceGroup === null ? null : normalizedPriceGroup * pct
        };
    }

    if (+promo?.promo_pp) {
        return {
            price: +promo.promo_pp,
            price_group: null
        };
    }

    if (+promo?.promo_fixed) {
        const fixed = +promo.promo_fixed;
        const normalizedQty = numberOrDefault(qty, 0);

        const total = price * normalizedQty;
        const totalGroup = normalizedPriceGroup === null ? 0 : normalizedPriceGroup;

        let newTotal = total - fixed;
        let newTotalGroup = totalGroup - fixed;

        if (newTotal < 0) newTotal = 0;
        if (newTotalGroup < 0) newTotalGroup = 0;

        const totalPct = total > 0 ? newTotal / total : 0;
        const totalGroupPct = totalGroup > 0 ? newTotalGroup / totalGroup : 0;

        return {
            price: price * totalPct,
            price_group: normalizedPriceGroup !== null ? normalizedPriceGroup * totalGroupPct : null
        };
    }

    return { price, price_group: normalizedPriceGroup };
};

const toDateValue = (value: any): Date => {
    if (value instanceof Date) {
        return value;
    }

    if (typeof value === 'string') {
        if (value.includes('-')) {
            return parse(value, 'yyyy-MM-dd', new Date());
        }

        if (value.length === 8) {
            return parse(value, 'yyyyMMdd', new Date());
        }
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return new Date();
    return parsedDate;
};

export const format = (num: number, currency: string): string => {
    const symbol = getCurrencySymbol(currency, 'narrow');
    return formatCurrency(num, 'en-GB', symbol, currency);
};

export const filterOptions = (pack: Package, date?: Date, session?: Availability | any, message?: string): any[] => {
    let price = numberOrDefault(pack?.pprice, pack?.price);
    let price_group = pack?.price_group ?? null;
    const priceOptions = pack?.price_options || null;

    if (!priceOptions || !priceOptions.length) {
        return [{
            name: 'Participants',
            qty_requested: pack?.qty_requested ?? session?.qty_requested ?? 0,
            price,
            _price: price,
            __price: 'no',
            price_group,
            _price_group: price_group
        }];
    }

    const dayNum = date ? (date.getDay() + 6) % 7 : 0;

    const excluded: any[] = [];
    const validSessOptions: any[] = [];
    const validPackOptions: any[] = [];

    const sessOptions = Object.values(session?.price_options ?? []).filter((item: any) => {
        const avail = { day: true, session: true, start_date: true, end_date: true };

        if (item?.dow !== undefined && item?.dow?.length && date) {
            avail.day = item.dow.charAt(dayNum) !== '-';
        }

        if (item?.start_date && date) {
            const startDate = parse(item.start_date, 'yyyy-MM-dd', new Date());
            avail.start_date = isBefore(startDate, date) || isSameDay(startDate, date);
        }

        if (item?.end_date && date) {
            const endDate = parse(item.end_date, 'yyyy-MM-dd', new Date());
            avail.end_date = isAfter(endDate, date) || isSameDay(endDate, date);
        }

        if (item?.sessions && item.sessions.length && session?.session_id) {
            avail.session = !!item.sessions.find((val: any) => (val.session_id || val.id) === session.session_id);
        }

        if (item?.price_option_id && avail.day && avail.session && avail.start_date && avail.end_date) {
            validSessOptions.push(item.price_option_id);
        }

        if (item?.price_option_id && !(avail.day && avail.session && avail.start_date && avail.end_date)) {
            excluded.push(item.price_option_id);
        }

        return avail.day && avail.session && avail.start_date && avail.end_date;
    });

    const bkpOptions: any[] = [];

    let options = priceOptions.filter((item: any) => {
        const avail = { day: true, session: true, start_date: true, end_date: true };

        if (item?.dow && item.dow.length && date) {
            avail.day = item.dow.charAt(dayNum) !== '-';
        }

        if (item?.start_date && date) {
            const startDate = parse(item.start_date, 'yyyy-MM-dd', new Date());
            avail.start_date = isBefore(startDate, date) || isSameDay(startDate, date);
        }

        if (item?.end_date && date) {
            const endDate = parse(item.end_date, 'yyyy-MM-dd', new Date());
            avail.end_date = isAfter(endDate, date) || isSameDay(endDate, date);
        }

        if (item?.sessions && item.sessions.length && session?.session_id) {
            avail.session = !!item.sessions.find((val: any) => (val.session_id || val.id) === session.session_id);
        }

        const optMatches = sessOptions.filter((filt: any) => filt?.name === item?.name || filt?.name?.en === item?.name);

        if (optMatches.length >= 1) {
            item.cms_data = optMatches[0];
        }

        if (item?.cms_data?.price_option_id && avail.day && avail.session && avail.start_date && avail.end_date) {
            validPackOptions.push(item.cms_data.price_option_id);
        }

        if (!avail.day && avail.session && avail.start_date && avail.end_date) {
            bkpOptions.push(item);
        }

        return avail.day
            && avail.session
            && avail.start_date
            && avail.end_date
            && (!item?.cms_data?.price_option_id
                || (item.cms_data.price_option_id && !excluded.find((excl: any) => excl === item.cms_data.price_option_id)));
    });

    const missing = validSessOptions.filter(value => !validPackOptions.includes(value));
    missing.forEach((miss: any) => {
        sessOptions.forEach((filt: any) => {
            if (filt?.price_option_id === miss) {
                options.push(filt);
            }
        });
    });

    options = options.filter((it: any) => !(it?.name === 'unit' || it?.name?.en === 'unit'));

    options = options.sort((it: any, it2: any) => {
        if ((it?.min_pax ?? 0) < (it2?.min_pax ?? 0)) return -1;
        if ((it?.min_pax ?? 0) > (it2?.min_pax ?? 0)) return 1;

        const itName = it?.name?.en ?? it?.name ?? '';
        const it2Name = it2?.name?.en ?? it2?.name ?? '';
        if (itName < it2Name) return -1;
        if (itName > it2Name) return 1;

        if ((it?.label ?? 'zz') < (it2?.label ?? 'zz')) return -1;
        if ((it?.label ?? 'zz') > (it2?.label ?? 'zz')) return 1;

        return 0;
    });

    const peakOptions = options.filter((it: any) => it?.label?.trim?.()?.toLowerCase?.().indexOf('same day peak') >= 0);
    const offPeakOptions = options.filter((it: any) => it?.label?.trim?.()?.toLowerCase?.().indexOf('peak') === 0);
    if (peakOptions.length && offPeakOptions.length) {
        options = options.filter((it: any) => it?.label?.trim?.()?.toLowerCase?.().indexOf('peak') !== 0);
    }

    if (options.length) {
        return options.map((item: any) => ({
            ...item,
            qty_requested: numberOrDefault(item?.qty_requested, pack?.qty_requested, session?.qty_requested, 0),
            price: numberOrDefault(item?.price, price),
            _price: numberOrDefault(item?.price, price),
            __price: 'yes',
            price_group: numberOrDefault(item?.price_group, price_group),
            _price_group: numberOrDefault(item?.price_group, price_group)
        }));
    }

    if (bkpOptions.length) {
        return bkpOptions.map((item: any) => ({
            ...item,
            qty_requested: numberOrDefault(item?.qty_requested, 0),
            price: numberOrDefault(item?.price, price),
            _price: numberOrDefault(item?.price, price),
            __price: 'yes',
            price_group: numberOrDefault(item?.price_group, price_group),
            _price_group: numberOrDefault(item?.price_group, price_group)
        }));
    }

    return [{
        name: 'Participants',
        qty_requested: pack?.qty_requested ?? session?.qty_requested ?? 0,
        price,
        _price: price,
        __price: 'no',
        price_group,
        _price_group: price_group
    }];
};

export const getOptionsTotal = (currency: string, pack: any, date?: Date, session?: any): {
    total: number;
    cms_total: number;
} => {
    if (!pack?.price_options || pack.price_options.length === 0) {
        const qtyRequested = numberOrDefault(pack?.initial_qty_requested, pack?.qty_requested);
        if (qtyRequested > 0 && pack?.price_group) {
            return { total: +pack.price_group, cms_total: +pack.price_group };
        }
        return {
            total: qtyRequested * numberOrDefault(pack?.price, 0),
            cms_total: numberOrDefault(pack?.qty_requested, 0) * numberOrDefault(pack?.price, 0)
        };
    }

    const options = filterOptions(pack, date, session);

    const prices = (options || []).reduce((acc: number[], opt: any) => {
        opt.pos = (pack?.price_options || []).filter((item: any) => opt?.name === item?.name || opt?.name?.en === item?.name);

        if (opt.pos.length === 1) {
            opt.qty_requested = opt.pos[0].qty_requested;
            if (opt.pos[0].initial_qty_requested) {
                opt.initial_qty_requested = opt.pos[0].initial_qty_requested;
            }
        }

        const qtyRequested = numberOrDefault(opt?.initial_qty_requested, opt?.qty_requested);
        if (qtyRequested > 0 && opt?.price_group) {
            return [...acc, numberOrDefault(opt?.price_group, 0) * 1];
        }

        return [...acc, numberOrDefault(opt?.price, pack?.price) * qtyRequested];
    }, []);

    const cmsPrices = options.reduce((acc: number[], optz: any) => {
        const opt = optz?.cms_data ?? optz;
        let alts = opt?.alts || [];

        if (opt?.cms_data?.alts) {
            alts = opt.cms_data.alts;
        }

        let altPrice = numberOrDefault(opt?.price, optz?.price, pack?.price);
        let altPriceGroup: number | null = numberOrDefault(opt?.price_group, optz?.price_group, pack?.price_group);
        if ((altPriceGroup ?? 0) === 0) altPriceGroup = null;

        if (alts.length > 0) {
            const alt = alts.reduce((prev: any, curr: any) => {
                if (optz?.qty_requested) opt.qty_requested = optz.qty_requested;
                if (!opt?.qty_requested || +opt.qty_requested < +curr?.min_pax) return prev;
                if (!prev) return curr;
                if (+prev.min_pax <= +curr.min_pax) return curr;
                return prev;
            }, null);

            if (alt !== null && alt !== undefined) {
                altPrice = numberOrDefault(alt?.price, altPrice);
                if (alt?.price_group) altPriceGroup = numberOrDefault(alt?.price_group, altPriceGroup ?? 0);
            }
        }

        const qtyRequested = numberOrDefault(opt?.qty_requested, optz?.qty_requested);
        if (qtyRequested > 0 && altPriceGroup) return [...acc, numberOrDefault(altPriceGroup, 0) * 1];
        return [...acc, numberOrDefault(altPrice, opt?.price, pack?.price) * qtyRequested];
    }, []);

    const total = prices.reduce((tot, item) => tot + item, 0);
    const cmsTotal = cmsPrices.reduce((tot, item) => tot + item, 0);

    return { total, cms_total: cmsTotal };
};

export const getOptionsMinMax = (currency: string, pack: any, date?: Date, session?: any): {
    price: string;
    price_label: string;
    min: number;
    max: number;
    price_group: string;
    min_group: number;
    max_group: number;
} => {
    const prices = filterOptions(pack, date, session).reduce((acc: number[], opt: any) => {
        let alts: number[] = [];
        if (opt?.alts) {
            alts = opt.alts
                .filter((item: any) => item?.price)
                .map((alt: any) => +numberOrDefault(alt?.price_calc, alt?.price));
        }

        return [...acc, ...alts, +numberOrDefault(opt?.price_calc, opt?.price)];
    }, []);

    const priceGroups = filterOptions(pack, date, session).reduce((acc: number[], opt: any) => {
        let alts: number[] = [];
        if (opt?.alts) {
            alts = opt.alts
                .filter((item: any) => item?.price_group)
                .map((alt: any) => +numberOrDefault(alt?.price_group, 0));
        }

        return [...acc, ...alts, +numberOrDefault(opt?.price_group, 0)].filter((it: number) => it !== null);
    }, []);

    const min = Math.min(...prices);
    const max = Math.max(...prices);

    const minGroup = Math.min(...priceGroups);
    const maxGroup = Math.max(...priceGroups);

    const minGroupText = Number.isFinite(minGroup) ? format(minGroup, currency) : '';
    const maxGroupText = Number.isFinite(maxGroup) ? format(maxGroup, currency) : '';
    const price = min === max ? format(min, currency) : `${format(min, currency)} - ${format(max, currency)}`;
    const nonZeroGroupPrices = priceGroups.filter(value => Number.isFinite(value) && value !== 0);
    let priceLabel = price;
    if (nonZeroGroupPrices.length) {
        const groupMin = Math.min(...nonZeroGroupPrices);
        const groupMax = Math.max(...nonZeroGroupPrices);
        const groupPrice = groupMin === groupMax
            ? format(groupMin, currency)
            : `${format(groupMin, currency)} - ${format(groupMax, currency)}`;
        priceLabel = `${groupPrice} per group`;
    }

    return {
        price,
        price_label: priceLabel,
        price_group: minGroup && maxGroup && minGroup === maxGroup ? minGroupText : `${minGroupText} - ${maxGroupText}`,
        min,
        max,
        min_group: minGroup,
        max_group: maxGroup
    };
};

export const getDow = (items: any[]): string => {
    if (!items || !items.length) return 'MTWTFSS';

    const options = items.map((val: any) => val?.dow);
    const filtered = options.filter(Boolean);

    if (options.length !== filtered.length) return 'MTWTFSS';

    return 'MTWTFSS'.split('').map((day, i) => {
        const open = options.map((opt: string) => opt.charAt(i)).filter((opt: string) => opt !== '-');
        return !open.length ? '-' : day;
    }).join('');
};

export const getParameterByName = (name: string, url?: string) => {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');

    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
    const results = regex.exec(url);

    if (!results || !results[2]) return null;
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const addMissingProps = (session: Availability, venue: Venue, pack: Package): void => {
    if (!session?.price_options || session.price_options.length === 0) return;
    if (typeof session?.price_options === 'string') return;

    session.price_options.forEach((po: any) => {
        pack?.price_options?.forEach((pa: any) => {
            if (pa?.cms_data?.price_option_id) {
                pa.price_option_id = pa.cms_data.price_option_id;
            }

            if (pa?.price_option_id === po?.price_option_id && pa?.qty_requested !== null && pa?.qty_requested !== undefined) {
                po.qty_requested = pa.qty_requested;
            }
        });

        venue?.packages?.forEach((vepa: any) => {
            if (vepa?.package_id !== pack?.package_id) return;
            if (!vepa?.price_options || vepa.price_options.length === 0) return;

            const papo = vepa.price_options.filter((it: any) => it?.price_option_id === po?.price_option_id)[0];
            if (papo?.dow) po.dow = papo.dow;
        });
    });
};

export const applyPromo = (promo: any, date: any, venue: Venue, pack: any, session: any, priceOptions: any[], qty: number) => {
    if (!promo) {
        if (!priceOptions || priceOptions.length === 0) {
            pack._price_calc = null;
            pack.price_calc = null;
        }

        return (priceOptions || []).map((item: any) => {
            if (item?.alts) item.alts = item.alts.map((val: any) => ({ ...val, price_calc: null, price_group_calc: null }));

            return {
                ...item,
                price_calc: null,
                _price_calc: null,
                price_group_calc: null,
                _price_group_calc: null
            };
        });
    }

    if (promo?.invalid) return promo?.invalid_reason ?? 'Promocode is not valid';
    if (!+promo?.bookings) return 'Promocode is not valid for bookings';

    const eventDate = toDateValue(date);

    if (promo?.lead && session?.start_time) {
        const userTime = toZonedTime(new Date(), venue?.timezone);
        const sessionTime = toZonedTime(
            parse(`${_format(eventDate, 'yyyy-MM-dd')} ${session.start_time}`, 'yyyy-MM-dd HH:mm', new Date()),
            venue?.timezone
        );

        if (isBefore(sessionTime, userTime)) return 'Promocode is invalid lead time';
    }

    if (
        (promo?.venue_id && venue?.venue_id !== promo.venue_id)
        || (promo?.venue_ids && promo.venue_ids.length && !promo.venue_ids.includes(venue?.venue_id))
    ) return 'Promocode is not valid for this venue';

    if (
        (promo?.activity_id && pack?.activity_id !== promo.activity_id)
        || (promo?.activity_ids && promo.activity_ids.length && !promo.activity_ids.includes(pack?.activity_id))
    ) return 'Promocode is not valid for this activity';

    if (
        (promo?.package_id && pack?.package_id !== promo.package_id)
        || (promo?.package_ids && promo.package_ids.length && !promo.package_ids.includes(pack?.package_id))
    ) return 'Promocode is not valid for this package';

    if (
        (promo?.session_id && session?.session_id !== promo.session_id)
        || (promo?.session_ids && promo.session_ids.length && !promo.session_ids.includes(session?.session_id))
    ) return 'Promocode is not valid for this session';

    const redemptionFrom = promo?.start_date ? parse(promo.start_date, 'yyyy-MM-dd', new Date()) : null;
    const redemptionTo = promo?.end_date ? parse(promo.end_date, 'yyyy-MM-dd', new Date()) : null;
    const eventFrom = promo?.start_date_event ? parse(promo.start_date_event, 'yyyy-MM-dd', new Date()) : null;
    const eventTo = promo?.end_date_event ? parse(promo.end_date_event, 'yyyy-MM-dd', new Date()) : null;
    const weekday = getISODay(eventDate) - 1;
    const now = new Date();
    const dateFormat = 'dd MMM yyyy';

    if (promo?.dow && promo.dow.charAt(weekday) === '-') return 'This promocode is not valid on this day of the week';
    if (promo?.min_pax && +promo.min_pax > qty) return 'This promocode is not valid on this group size';
    if (redemptionFrom && isBefore(now, redemptionFrom)) return `This promocode is valid from ${_format(redemptionFrom, dateFormat)}`;
    if (redemptionTo && isAfter(now, redemptionTo)) return `This promocode is valid until ${_format(redemptionTo, dateFormat)}`;
    if (eventFrom && isBefore(eventDate, eventFrom)) return `This promocode is valid for bookings after ${_format(eventFrom, dateFormat)}`;
    if (eventTo && isAfter(eventDate, eventTo)) return `This promocode is valid for bookings before ${_format(eventTo, dateFormat)}`;

    if (promo?.exclusions && promo.exclusions.length) {
        const eventDateStr = _format(eventDate, 'yyyy-MM-dd');
        const eventDateDay = _format(eventDate, 'EEEE');

        const excluded = promo.exclusions.reduce((acc: boolean, item: any) => {
            if (acc) return acc;
            const days = Array.isArray(item?.days) ? item.days : [];
            return item?.date === eventDateStr || days.includes(eventDateDay);
        }, false);

        if (excluded) return `This promo is not valid on ${_format(eventDate, 'dd MMM yyyy')}`;
    }

    const safePriceOptions = priceOptions || [];
    const maxDiscount = +promo?.max_discount;
    const total = safePriceOptions.reduce((acc: number, item: any) => acc + numberOrDefault(item?.price, 0) * numberOrDefault(item?.qty_requested, 0), 0);

    if (!safePriceOptions.length) {
        const discount = applyDiscount(promo, numberOrDefault(pack?.price, 0), numberOrDefault(pack?.price_group, 0), qty);
        pack._price_calc = discount.price;
        pack.price_calc = discount.price;
        pack.price_group_calc = discount.price_group;
        pack._price_group_calc = discount.price_group;
    }

    let priceOptionsCalc = safePriceOptions.map((item: any) => {
        const discount = applyDiscount(
            promo,
            +(item?.price === '' || item?.price == null ? pack?.price : item?.price),
            +(item?.price_group === '' || item?.price_group == null ? pack?.price_group : item?.price_group),
            qty
        );

        if (item?.alts) {
            item.alts = item.alts.map((val: any) => {
                const altDiscount = applyDiscount(promo, numberOrDefault(val?.price, 0), val?.price_group, val?.qty);
                return { ...val, price_calc: altDiscount.price, price_group_calc: altDiscount.price_group };
            });
        }

        return {
            ...item,
            price_calc: discount.price,
            _price_calc: discount.price,
            price_group_calc: discount.price_group,
            _price_group_calc: discount.price_group
        };
    });

    const discount = total - priceOptionsCalc.reduce((acc: number, item: any) => acc + numberOrDefault(item?.qty_requested, 0) * numberOrDefault(item?.price_calc, 0), 0);
    if (discount <= 0) return `Discount couldn't be applied to ${pack?.name}`;

    if (maxDiscount > 0 && discount > maxDiscount) {
        const promoReduction = maxDiscount / total;

        priceOptionsCalc = safePriceOptions.map((item: any) => {
            if (item?.alts) {
                item.alts = item.alts.map((val: any) => ({
                    ...val,
                    price_calc: numberOrDefault(val?.price_calc, val?.price, 0) / promoReduction,
                    price_group_calc: numberOrDefault(val?.price_group_calc, val?.price_group, 0) / promoReduction
                }));
            }

            return {
                ...item,
                price_calc: numberOrDefault(item?.price_calc, item?.price, 0) / promoReduction,
                _price_calc: numberOrDefault(item?.price_calc, item?._price, 0) / promoReduction,
                price_group_calc: numberOrDefault(item?.price_group_calc, item?.price_group, 0) / promoReduction,
                _price_group_calc: numberOrDefault(item?.price_group_calc, item?._price_group, 0) / promoReduction,
            };
        });
    }

    return priceOptionsCalc;
};
